import * as Braze from '@braze/web-sdk'

/*
 * The following utilities are web specific and can not be co-located with our shared
 * cross-platform utilities in `/braze/braze.utils` because they require imports from
 * `@braze/web-sdk` that conflict with `@braze/react-native-sdk`. A platform specific utility file
 * like `/braze/braze.utils.web` would unfortunately also make the shared utilities in
 * `/braze/braze.utils` inaccessible to web. However, these can be imported conflict free with
 * `@braze/react-native-sdk` dependencies in Jest tests since Jest renders in a "web DOM-like" and
 * were native dependencies are mocked.
 */

import {TypedBrazeContentCard} from 'src/lib/braze/braze.types'

export type WebContentCard = Braze.ImageOnly | Braze.CaptionedImage | Braze.ClassicCard

type BaseSchema = Omit<TypedBrazeContentCard, 'type' | 'title' | 'cardDescription'>
// Maps the shared properties amongst all Braze Content Card types from the web SDK in one place.
function mapWebCardToBaseSchema(webContentCard: WebContentCard): BaseSchema {
  const baseSchema: BaseSchema = {
    id: webContentCard.id ?? '',
    created: webContentCard.created?.getTime() ?? 0,
    expiresAt: webContentCard.expiresAt?.getTime() ?? 0,
    viewed: webContentCard.viewed,
    clicked: webContentCard.clicked,
    pinned: webContentCard.pinned,
    dismissed: webContentCard.dismissed,
    dismissible: webContentCard.dismissible,
    url: webContentCard?.url,
    openURLInWebView: false,
    isControl: webContentCard.isControl,
    extras: webContentCard?.extras,
  }

  return baseSchema
}

/**
 * Maps the `@braze/web-sdk` response to match `@braze/react-native-sdk` so we can work with the
 * same data structure across all platforms.
 */
export function mapWebContentCardsToCrossPlatformContentCards(
  cards: Braze.ContentCards['cards'],
): TypedBrazeContentCard[] {
  return cards.reduce<TypedBrazeContentCard[]>((previousValue, currentValue) => {
    if (currentValue instanceof Braze.ClassicCard) {
      previousValue.push({
        ...mapWebCardToBaseSchema(currentValue),
        type: 'Classic',
        image: currentValue.imageUrl,
        title: currentValue.title,
        cardDescription: currentValue.description,
      })
    }
    if (currentValue instanceof Braze.CaptionedImage) {
      previousValue.push({
        ...mapWebCardToBaseSchema(currentValue),
        type: 'Captioned',
        image: currentValue.imageUrl ?? '',
        imageAspectRatio: currentValue.aspectRatio ?? 0,
        title: currentValue.title,
        cardDescription: currentValue.description,
      })
    }
    if (currentValue instanceof Braze.ImageOnly) {
      previousValue.push({
        ...mapWebCardToBaseSchema(currentValue),
        type: 'ImageOnly',
        image: currentValue?.imageUrl ?? '',
        imageAspectRatio: currentValue.aspectRatio ?? 0,
      })
    }

    return previousValue
  }, [])
}
